import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useCapabilitySearch } from "@/api/capabilities";
import type { FeedbackConfig } from "@/api/feedback";
import { Close as CloseIcon } from "@/components/icons";
import { CapabilityCardView } from "@/features/ask/AskResults";
import type { AskTab } from "@/features/ask/askEvents";
import { MyRequests } from "@/features/ask/MyRequests";
import { RequestForm } from "@/features/ask/RequestForm";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const TITLE_ID = "ask-panel-title";

/** The Ask panel: answers from the capability catalogue, and — when the
 *  help has none — a request or a problem report, which are feedback rows.
 *  A right-side sheet, so the page stays visible. */
export function AskPanel(props: {
  open: boolean;
  tab: AskTab;
  onTab: (tab: AskTab) => void;
  onClose: () => void;
  feedbackConfig: FeedbackConfig | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const phone = useMediaQuery(useTheme().breakpoints.down("sm"));
  // Counts opens (adjusting state while rendering, React's pattern for
  // "reset when a prop changes"), so a new open remounts the content.
  const [session, setSession] = React.useState(0);
  const [wasOpen, setWasOpen] = React.useState(props.open);
  if (props.open !== wasOpen) {
    setWasOpen(props.open);
    if (props.open) setSession(session + 1);
  }
  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      // Above the fixed app bar (drawer + 1), like the notifications drawer.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-labelledby": TITLE_ID,
          sx: { width: phone ? "100%" : 440, maxWidth: "100vw" },
        },
      }}
    >
      <Stack sx={{ p: 2, gap: 1.5, height: "100%" }}>
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Typography id={TITLE_ID} variant="h6" component="h2" sx={{ flex: 1 }}>
            {t("ask.open")}
          </Typography>
          <IconButton aria-label={t("ask.close")} onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Tabs value={props.tab} onChange={(_e, next: AskTab) => props.onTab(next)}>
          <Tab value="ask" label={t("ask.tab_ask")} />
          <Tab value="mine" label={t("ask.tab_mine")} />
        </Tabs>
        {/* Keyed on the open count: every open starts clean, while the
            content stays put through the closing slide (the Drawer
            unmounts it once that ends). The Ask tab stays mounted while
            My requests shows, so the question survives a tab switch. */}
        <Box key={session} sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          <Box hidden={props.tab !== "ask"}>
            <AskBody onTab={props.onTab} onClose={props.onClose} feedbackConfig={props.feedbackConfig} />
          </Box>
          {props.tab === "mine" && <MyRequests enabled onNavigate={props.onClose} />}
        </Box>
      </Stack>
    </Drawer>
  );
}

function AskBody(props: {
  onTab: (tab: AskTab) => void;
  onClose: () => void;
  feedbackConfig: FeedbackConfig | undefined;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [question, setQuestion] = React.useState("");
  const [form, setForm] = React.useState<null | "idea" | "bug">(null);
  const [sent, setSent] = React.useState<null | "idea" | "bug">(null);
  const q = useDebouncedValue(question.trim(), 250);
  const search = useCapabilitySearch(q, location.pathname, i18n.language, !form && !sent);
  const canRequest = props.feedbackConfig?.enabled === true;

  if (sent) {
    return (
      <Stack sx={{ gap: 1.5 }}>
        <Alert severity="success">{t(sent === "idea" ? "ask.sent" : "ask.sent_problem")}</Alert>
        {sent === "idea" && (
          <Button onClick={() => props.onTab("mine")} sx={{ alignSelf: "flex-start" }}>
            {t("ask.view_mine")}
          </Button>
        )}
      </Stack>
    );
  }
  if (form && props.feedbackConfig) {
    return (
      <RequestForm
        kind={form}
        question={question.trim()}
        config={props.feedbackConfig}
        onSent={() => {
          setSent(form);
          setForm(null);
        }}
        onBack={() => setForm(null)}
      />
    );
  }
  const shown = q.length >= 2 ? search.data : undefined;
  return (
    <Stack sx={{ gap: 1.5, pt: 0.5 }}>
      <TextField
        autoFocus
        fullWidth
        placeholder={t("ask.placeholder")}
        slotProps={{ htmlInput: { "aria-label": t("ask.placeholder") } }}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
      />
      {/* Answers arrive as the user types: announce them, politely. */}
      <Stack aria-live="polite" sx={{ gap: 1.5 }}>
        {shown && !shown.index_ok && <Alert severity="info">{t("ask.unavailable")}</Alert>}
        {search.isError && q.length >= 2 && <Alert severity="info">{t("ask.unavailable")}</Alert>}
        {shown?.index_ok && shown.results.length === 0 && (
          <Typography color="text.secondary">{t("ask.no_results")}</Typography>
        )}
        {shown?.results.map((card) => (
          <CapabilityCardView key={card.id} card={card} onNavigate={props.onClose} />
        ))}
      </Stack>
      {canRequest && q.length >= 2 && (
        <Box component="footer" sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("ask.didnt_find")}
          </Typography>
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
            <Button variant="contained" size="small" onClick={() => setForm("idea")}>
              {t("ask.request_this")}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setForm("bug")}>
              {t("ask.report_problem")}
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
